import { useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client';
import SentryService from '@/services/sentry/Sentry.service';
import {
  MutationResult,
  MutationUpdateUserTimeZoneArgs,
} from '@/shared/types/generated';
import { UPDATE_USER_TIME_ZONE } from '@/shared/mutations';
import { useGetUser } from '@/shared/hooks/useGetUser';
import { getCurrentTimeZone } from '@/shared/utils';

export function useSaveUserTimeZone() {
  const { user } = useGetUser();
  const hasUpdatedTimeZone = useRef(false);

  const [updateUserTimeZone] = useMutation<
    MutationResult<'updateUserTimeZone'>,
    MutationUpdateUserTimeZoneArgs
  >(UPDATE_USER_TIME_ZONE);

  useEffect(() => {
    const saveTimeZone = async () => {
      // Skip if already updated or user not loaded
      if (hasUpdatedTimeZone.current || !user) {
        return;
      }

      // Skip if user already has a timezone
      if (user.timeZone) {
        hasUpdatedTimeZone.current = true;
        return;
      }

      try {
        const currentTimeZone = getCurrentTimeZone();

        await updateUserTimeZone({
          variables: {
            input: {
              timeZone: currentTimeZone,
            },
          },
        });

        hasUpdatedTimeZone.current = true;
        __DEV__ && console.log(`Updated user timezone to: ${currentTimeZone}`);
      } catch (error) {
        SentryService.captureError('Error auto-updating user timezone', error);
      }
    };

    saveTimeZone();
  }, [user, updateUserTimeZone]);
}
